import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  ICreateFolder,
  Template,
  TemplateFolder,
  TemplateInfo,
  TemplateListResponse,
  TemplateRequest,
  TemplateResponse,
} from '../../../create-surveys/layout/template-modal/template-modal.interface';
import { iif, Subject } from 'rxjs';
import { QUALIFICATIONS_CATEGORIES } from '../../constants/qualification-categories';
import { Constants } from '../../constants/create-survey.constants';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../../services/auth/auth.service';
import {
  BuyerSurveyResponse,
  SupplierResponseInterface,
  SurveyRequest,
} from '../../interfaces/survey.interface';
import { EnvironmentConfig } from '../../interfaces/environment.interface';
import { User } from '../../interfaces/user.interface';
import { CountryInterface } from '../../interfaces/create-surveys.interface';

@Injectable({
  providedIn: 'root',
})
export class TemplateService {
  private readonly _baseUrl = this._env.environment.pureSpectrum.url;

  private readonly _newTemplateSubject = new Subject();

  private readonly _supplierInfoSubject = new Subject<
    SupplierResponseInterface[]
  >();
  public supplierInfo = this._supplierInfoSubject.asObservable();

  constructor(
    private _http: HttpClient,
    private _authService: AuthService,
    @Inject('ENV') private _env: EnvironmentConfig
  ) {}

  public allTemplates(shouldFetchSelfOwnedTemplates = false) {
    const is_service_buyer = this._authService.isLoggedInAsServiceBuyer();
    const queryParamObj = {
      UI: 1,
      is_service_buyer,
      self_owned: shouldFetchSelfOwnedTemplates,
    };
    const params = new HttpParams({ fromObject: queryParamObj });
    return this._http.get<TemplateListResponse[]>(
      `${this._baseUrl}/buyers/v2/templates`,
      { params }
    );
  }

  public sendSupplierInfo(suppliers: SupplierResponseInterface[]): void {
    this._supplierInfoSubject.next(suppliers);
  }

  /**
   * Get a observable that will emit the template list at first subscription and every time a new template is created
   */
  public getUpdatedTemplateList() {
    return this._newTemplateSubject.pipe(
      startWith(1),
      switchMap(() => this.allTemplates())
    );
  }

  public getById(id: number) {
    return this._http
      .get<TemplateResponse>(`${this._baseUrl}/buyers/v2/templates/${id}?UI=1`)
      .pipe(
        map(this._applyQuotaCategory),
        map(this._handleAdvancedTargetingQualifications)
      );
  }

  public saveTemplate(payload: TemplateRequest) {
    return this._http
      .post<TemplateResponse>(
        `${this._baseUrl}/buyers/v2/templates?UI=1`,
        payload
      )
      .pipe(tap(() => this._newTemplateSubject.next()));
  }

  public updateTemplate(id: string, payload: TemplateRequest) {
    return this._http.put<TemplateResponse>(
      `${this._baseUrl}/buyers/v2/templates/${id}?UI=1`,
      payload
    );
  }

  public saveOrUpdateTemplate(payload: TemplateRequest, id?: string) {
    return iif(
      () => id != undefined,
      this.updateTemplate(id!, payload),
      this.saveTemplate(payload)
    );
  }

  public createFolder(payload: ICreateFolder) {
    return this._http.post<TemplateFolder>(
      `${this._baseUrl}/buyers/v2/templates/folders`,
      payload
    );
  }

  public deleteTemplate(template: TemplateListResponse) {
    return this._http.delete<TemplateListResponse>(
      `${this._baseUrl}/buyers/v2/templates/${template.id}`,
      {}
    );
  }

  public deleteTemplateFolder(folder: TemplateListResponse) {
    return this._http.delete<TemplateListResponse>(
      `${this._baseUrl}/buyers/v2/templates/folders/${folder.id}`,
      {}
    );
  }

  private _applyQuotaCategory({
    folder,
    template,
    is_suppliers_list,
    suppliers_data,
    is_mc_template,
  }: TemplateResponse): TemplateResponse {
    const quotas = template.quotas ?? [];

    quotas.forEach((quota) => {
      const criteria = quota.criteria[0];
      const conditions =
        (criteria.condition_codes?.length || criteria.range_sets?.length) ?? 0;

      if (conditions == 1 && !quota.quota_category) {
        quota.quota_category = QUALIFICATIONS_CATEGORIES.LAYERED;
      } else if (conditions > 1 && !quota.quota_category) {
        quota.quota_category = QUALIFICATIONS_CATEGORIES.GROUPED;
      }
    });

    return {
      folder,
      template,
      is_suppliers_list,
      suppliers_data,
      is_mc_template,
    };
  }

  private _handleAdvancedTargetingQualifications({
    folder,
    template,
    is_suppliers_list,
    suppliers_data,
    is_mc_template,
  }: TemplateResponse): TemplateResponse {
    const qualifications = template.qualifications ?? [];

    qualifications.forEach((qualification) => {
      if (
        qualification.qualification_code >=
        Constants.ADVANCE_TARGETING_QUAL_ID_START_INDEX
      ) {
        qualification.data = (qualification.condition_names || [])
          .map((value) => Object.entries(value))
          .map(([[key, value]]) => ({
            id: key,
            text: value,
          })) as any;
      }
    });

    return {
      folder,
      template,
      is_suppliers_list,
      suppliers_data,
      is_mc_template,
    };
  }

  prepareSelectedTemplate(
    templateData: TemplateResponse
  ): TemplateListResponse {
    const {
      id,
      name,
      owner,
      parent: parent_folder,
      is_default,
      is_service_buyer,
    } = templateData.folder;
    return {
      id,
      name,
      owner,
      is_default,
      is_service_buyer,
      parent_folder,
      templates: [this.prepareSelectedTemplateInfo(templateData.template)],
    };
  }

  prepareTemplateFolderData(
    templateListResponse: TemplateListResponse
  ): TemplateFolder {
    const { id, name, is_default, is_service_buyer, owner } =
      templateListResponse;
    return {
      id,
      owner,
      name,
      is_default,
      is_service_buyer,
    };
  }

  prepareSelectedTemplateInfo(template: Template): TemplateInfo {
    const {
      id,
      name,
      owner,
      shared_with,
      is_owner,
      is_service_buyer = false,
    } = template;
    return {
      id,
      name,
      owner,
      shared_with,
      is_owner,
      is_service_buyer,
    };
  }

  prepareSurveyPayloadFromTemplateData(
    template: Template,
    surveyTitle: string,
    surveyDetails: BuyerSurveyResponse,
    user: User,
    isServiceBuyerLoggedIn: boolean,
    offerPrice?: number,
    mrgn?: number
  ): SurveyRequest {
    const { cmp: buyer_company, id: buyer_user_id } = user;
    const [languageShortCode, countryShortCode] =
      template.survey_localization.split('_');
    const {
      survey_category_code,
      survey_localization,
      completes_required,
      expected_ir,
      expected_loi,
      field_time,
      offer_price = 0,
      margin = 50,
      is_service_buyer,
    } = template;
    return {
      survey_title: `${surveyTitle} - MC-${countryShortCode}-${languageShortCode}`,
      click_balancing: Number(!!surveyDetails?.click_balancing),
      survey_category_code,
      survey_localization,
      completes_required,
      expected_ir,
      expected_loi,
      field_time,
      source: 'platform',
      mc_unique_id: surveyDetails?.mc_unique_id,
      buyer_company,
      buyer_user_id,
      template_id: template.id,
      qualifications: template.qualifications,
      quotas: template.quotas,
      ...(isServiceBuyerLoggedIn &&
        !is_service_buyer && {
          offer_price: offerPrice,
          margin_override: {
            enable: !!offerPrice,
            margin: mrgn || 50,
          },
        }),
      ...(isServiceBuyerLoggedIn &&
        is_service_buyer && {
          offer_price,
          margin_override: {
            enable: !!offer_price,
            margin,
          },
        }),
    };
  }

  getNotifyLocalizations(locales: string[], countries: CountryInterface[]) {
    return locales
      .filter((locale) => typeof locale === 'string')
      .map((locale) => this._getlocalizationName(locale.split('_'), countries));
  }

  private _getlocalizationName(
    [languageCode, countryCode]: string[],
    countries: CountryInterface[]
  ) {
    return countries
      .filter((country) => country.short_Code === countryCode)
      .reduce(
        (acc, country) =>
          `${country.name}-${country.lang
            .filter((language) => language.transalte_code === languageCode)
            .map((language) => language.name)}`,
        ''
      );
  }
}
